import { useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { Header } from "@/components/Header";
import { Stepper } from "@/components/Stepper";
import { DirectiveInput } from "@/components/DirectiveInput";
import { PleaseGate } from "@/components/PleaseGate";
import { HeardCard } from "@/components/HeardCard";
import { AnswerCard } from "@/components/AnswerCard";
import { HistoryDrawer } from "@/components/HistoryDrawer";
import { checkManners, streamAnswer, saveConversation } from "@/lib/api";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { Gate } from "@/components/Gate";
import AuthPage from "@/pages/AuthPage";
import Legal from "@/pages/Legal";
import { Subscribe, Account, PaymentResult } from "@/pages/Membership";
import Rewards from "@/pages/Rewards";
import GoldenGuide from "@/pages/GoldenGuide";
import PrintRewardChart from "@/pages/PrintRewardChart";
import AdminTestimonials from "@/pages/AdminTestimonials";
import GetTheApp from "@/pages/GetTheApp";
import JoinFamily from "@/pages/JoinFamily";
import ParentTips from "@/pages/ParentTips";
import { Footer } from "@/components/Footer";
import VoiceSetup from "@/pages/VoiceSetup";
import Family from "@/pages/Family";
import About from "@/pages/About";
import { useFamilyProfile } from "@/hooks/useFamilyProfile";
import { useAcornJar } from "@/hooks/useAcornJar";
import { MilestoneCelebration } from "@/components/MilestoneCelebration";
import SorrySteps from "@/pages/SorrySteps";
import PrintCards from "@/pages/PrintCards";
import Manners from "@/pages/Manners";
import MannersScenario from "@/pages/MannersScenario";
import CardLibrary from "@/pages/CardLibrary";
import DinnerGame from "@/pages/DinnerGame";
import PrintDinnerCards from "@/pages/PrintDinnerCards";
import PrintDinnerBox from "@/pages/PrintDinnerBox";
import PrintNote from "@/pages/PrintNote";

function Coach() {
  const age = "kid";
  const [step, setStep] = useState(0);
  const [text, setText] = useState("");
  const [original, setOriginal] = useState("");
  const [check, setCheck] = useState(null);
  const [answer, setAnswer] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [reward, setReward] = useState(null);
  const { profiles, profile, pickProfile } = useFamilyProfile();
  const { jar, refresh: refreshJar, milestone, dismissMilestone } = useAcornJar(profile);
  const reset = () => { setReward(null); setStep(0); setText(""); setCheck(null); setAnswer(""); setOriginal(""); };

  const runCheck = async () => {
    setLoading(true);
    if (!original) setOriginal(text);
    try {
      const c = await checkManners(text, age);
      setCheck(c);
      setStep(c.passed ? 2 : 1);
    } catch {
      toast.error("The manners coach is busy. Try again.");
    }
    setLoading(false);
  };

  const proceed = async () => {
    setStep(3);
    setAnswer("");
    setStreaming(true);
    try {
      await streamAnswer(text, age, (t) => setAnswer((a) => a + t));
    } catch {
      toast.error("AI couldn't answer. Try again.");
    }
    setStreaming(false);
  };

  const thanked = async (thanks) => {
    let saved;
    try {
      saved = await saveConversation({
        age, original: original || text, directive: text, heard: check.heard, answer, thanks,
        profile_id: profile?.id, profile_name: profile?.name,
      });
    } catch {
      toast.error("We couldn't save your acorn. Please check your connection and try again.");
      return;
    }
    setReward(saved);
    setStep(4);
    setTimeout(refreshJar, 3400);
  };

  return (
    <div className={`pp-app mode-${age}`} data-testid="app-root">
      <Header onHistory={() => setHistoryOpen(true)} onNew={reset}
        profiles={profiles} profile={profile} onPick={pickProfile} jar={jar} />
      <main className="pp-main">
        <Stepper step={step} />
        {step === 0 && <DirectiveInput age={age} text={text} setText={setText} onSubmit={runCheck} loading={loading} />}
        {step === 1 && <PleaseGate age={age} check={check} text={text} setText={setText} onRecheck={runCheck} loading={loading} />}
        {step === 2 && <HeardCard age={age} check={check} text={text} onProceed={proceed} onEdit={() => setStep(0)} />}
        {step >= 3 && <AnswerCard age={age} answer={answer} streaming={streaming} unlocked={step === 4} onThanks={thanked} reward={reward} />}
        {step === 4 && (
          <button data-testid="ask-another-button" className="pp-primary mt-6" onClick={reset}>Ask something else</button>
        )}
      </main>
      <Footer />
      <HistoryDrawer open={historyOpen} onOpenChange={setHistoryOpen} profile={profile} />
      <MilestoneCelebration milestone={milestone} name={profile?.name} onClose={dismissMilestone} />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
    <BrowserRouter>
      <Toaster position="top-center" richColors />
      <Routes>
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/signup" element={<AuthPage mode="signup" />} />
        <Route path="/terms" element={<Legal doc="terms" />} />
        <Route path="/privacy" element={<Legal doc="privacy" />} />
        <Route path="/disclaimer" element={<Legal doc="disclaimer" />} />
        <Route path="/about" element={<About />} />
        <Route path="/get-the-app" element={<GetTheApp />} />
        <Route path="/join" element={<JoinFamily />} />
        <Route path="/payment/success" element={<PaymentResult ok />} />
        <Route path="/payment/cancel" element={<PaymentResult ok={false} />} />
        <Route element={<Gate memberOnly={false} />}>
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/account" element={<Account />} />
        </Route>
        <Route element={<Gate />}>
        <Route path="/" element={<Coach />} />
        <Route path="/parents" element={<ParentTips />} />
        <Route path="/voice" element={<VoiceSetup />} />
        <Route path="/family" element={<Family />} />
        <Route path="/sorry" element={<SorrySteps />} />
        <Route path="/print/apology-cards" element={<PrintCards />} />
        <Route path="/print/cards" element={<PrintCards />} />
        <Route path="/manners" element={<Manners />} />
        <Route path="/cards" element={<CardLibrary />} />
        <Route path="/dinner" element={<DinnerGame />} />
        <Route path="/print/dinner-cards" element={<PrintDinnerCards />} />
        <Route path="/print/dinner-box" element={<PrintDinnerBox />} />
        <Route path="/print/note" element={<PrintNote />} />
        <Route path="/manners/:id" element={<MannersScenario />} />
        <Route path="/rewards" element={<Rewards />} />
        <Route path="/golden-acorns" element={<GoldenGuide />} />
        <Route path="/print/reward-chart" element={<PrintRewardChart />} />
        <Route path="/admin/testimonials" element={<AdminTestimonials />} />
        </Route>
      </Routes>
    </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
